import { use, useSyncExternalStore, useCallback } from 'react';
import {
  useModals,
  PlumaModalManagerContext,
  useSideNavState,
  useToggleSideNav,
} from '@customerio/pluma-components/react';
import { useCopilotSessionContext, useCopilotSidebar, useMasqAccessLevel } from '@/features/copilot';
import { CommandDialog } from './command-dialog';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useThemeContext } from '@/context/theme';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { hasFeatureFlag } from '@/services/auth/feature-flags';
import { usePermissions } from '@customerio/hydra-permissions/react';
// Persists the last typed query across open/close cycles so reopening restores it.
// Scoped to a workspace ID — stale queries from a previous workspace are discarded.
let _lastInputValue = '';
let _lastInputWorkspaceId: number | undefined;
let _openTimeMs: number | null = null;
let _lastActiveFilter: string = 'all';
let _lastActiveSubFilter: string | null = null;
export function resetCommandDialogState(workspaceId: typeof _lastInputWorkspaceId) {
  _lastInputValue = '';
  _lastInputWorkspaceId = workspaceId;
  _openTimeMs = null;
  _lastActiveFilter = 'all';
  _lastActiveSubFilter = null;
}

const persistInputValue = (value: string) => {
  _lastInputValue = value;
};

const persistActiveFilter = (value: string) => {
  _lastActiveFilter = value;
};

const persistActiveSubFilter = (value: string | null) => {
  _lastActiveSubFilter = value;
};

function getLastInputValue(workspaceId: number): string {
  if (_lastInputWorkspaceId !== workspaceId) {
    resetCommandDialogState(workspaceId);
  }
  return _lastInputValue;
}
function getCloseTrackingData(workspaceId: number) {
  const hadQuery = getLastInputValue(workspaceId).trim().length > 0;
  const timeOpenMs = _openTimeMs == null ? 0 : Date.now() - _openTimeMs;

  return {
    hadQuery,
    query: getLastInputValue(workspaceId).trim(),
    timeOpenMs,
    activeFilter: _lastActiveFilter,
    activeSubFilter: _lastActiveSubFilter,
  };
}

const noopSubscribe = () => () => {};
const emptySnapshot = () => [];

/**
 * CommandDialog is mounted by Pluma's managed-modal layer (alongside the main app shell, not
 * inside the authenticated layout outlet). The CopilotSession/CopilotSidebar contexts only wrap
 * authenticated content, so they are NOT ancestors of the modal — anything depending on them must
 * be captured in this hook (which runs under the authenticated tree) and passed through
 * CommandDialogData:
 *   - session, openCopilot  (CopilotSessionProvider / CopilotSidebarProvider)
 *
 * Auth (useAuth, store-backed), permissions, the current workspace, the active bundle, feature
 * flags, the root QueryClient, TanStack Router, and SafeNavigate all resolve inside the modal
 * directly now — global stores or providers mounted above the root Pluma provider (FPS-3066).
 *
 * The remaining values below are still captured here and threaded as data; that is no longer
 * required for them to resolve, so it can be simplified later — but keeping them as an open()-time
 * snapshot from the authenticated tree is harmless:
 *   - workspace             (snapshot of the workspace active when the dialog opened)
 *   - track                 (useAnalytics)
 *   - isMasqWithoutAccess   (useMasqAccessLevel)
 */
export function useCommandDialog() {
  const modalApi = use(PlumaModalManagerContext);
  const modals = useSyncExternalStore(modalApi?.subscribe ?? noopSubscribe, modalApi?.getSnapshot ?? emptySnapshot);
  const isOpen = modals.some((m) => m.modal === CommandDialog && m.state.isOpen === true);

  const modalsApi = useModals();
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const { colorScheme, setColorScheme } = useThemeContext();
  const sideNavState = useSideNavState();
  const toggleSideNav = useToggleSideNav();
  const { isMasqWithoutAccess } = useMasqAccessLevel();
  const { account } = useAuth();
  const isBuilder = isBuilderPlan(account);
  const { hasPermission, hasAnyPermission } = usePermissions();

  const open = useCallback(
    (options?: { initialInputValue?: string }) => {
      const workspaceId = workspace?.id ?? 0;

      // Always call getLastInputValue first so the workspace-change guard fires
      // (resetting _lastActiveFilter to 'all' and updating _lastInputWorkspaceId)
      // before we read _lastActiveFilter or apply any override from options.
      // Do NOT short-circuit with ??, even when initialInputValue is provided.
      const persistedInputValue = getLastInputValue(workspaceId);
      const initialInputValue = options?.initialInputValue ?? persistedInputValue;

      _lastInputValue = initialInputValue;
      _openTimeMs = Date.now();

      modalsApi.open(CommandDialog, {
        session,
        workspace,
        initialInputValue,
        initialActiveFilter: _lastActiveFilter,
        initialActiveSubFilter: _lastActiveSubFilter,
        persistInputValue,
        persistActiveFilter,
        persistActiveSubFilter,
        track,
        openCopilot,
        colorScheme,
        setColorScheme,
        sideNavState,
        toggleSideNav,
        isMasqWithoutAccess,
        isBuilder,
        guard: { hasPermission, hasAnyPermission, account, hasFeatureFlag },
      });
    },
    [
      modalsApi,
      session,
      workspace,
      track,
      openCopilot,
      colorScheme,
      setColorScheme,
      sideNavState,
      toggleSideNav,
      isMasqWithoutAccess,
      isBuilder,
      hasPermission,
      hasAnyPermission,
      account,
    ],
  );

  const close = useCallback(() => {
    modalsApi.closeAllOfType(CommandDialog);
  }, [modalsApi]);

  const workspaceId = workspace?.id ?? 0;
  const getCloseTrackingSnapshot = useCallback(() => getCloseTrackingData(workspaceId), [workspaceId]);

  return { isOpen, open, close, getCloseTrackingSnapshot };
}
